import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Switch } from '../../components/ui';
import TodosMonthlyView from '../crm-todos/TodosMonthlyView';
import TodosWeeklyView from '../crm-todos/TodosWeeklyView';
import TodosDailyView from '../crm-todos/TodosDailyView';

dayjs.extend(isoWeek);

type ViewMode = 'month' | 'week' | 'day';

const STORAGE_KEY = 'appointments-calendar-view';

type StoredState = {
  viewMode: ViewMode;
  date: string;
  showOnlyOpen: boolean;
  showMyAppointmentsOnly: boolean;
};

function readStoredState(): Partial<StoredState> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<StoredState>;
    if (parsed.viewMode && !['month', 'week', 'day'].includes(parsed.viewMode)) delete parsed.viewMode;
    if (parsed.date && !dayjs(parsed.date).isValid()) delete parsed.date;
    return parsed;
  } catch {
    return {};
  }
}

function writeStoredState(s: StoredState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // ignore
  }
}

const CalendarView: React.FC = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const stored = readStoredState();

  const [viewMode, setViewMode] = useState<ViewMode>(stored.viewMode ?? 'month');
  const [currentDate, setCurrentDate] = useState<Dayjs>(() => {
    if (stored.date && dayjs(stored.date).isValid()) return dayjs(stored.date).startOf('day');
    return dayjs().startOf('day');
  });
  const [showOnlyOpen, setShowOnlyOpen] = useState(stored.showOnlyOpen ?? true);
  const [showMyAppointmentsOnly, setShowMyAppointmentsOnly] = useState(stored.showMyAppointmentsOnly ?? true);

  useEffect(() => {
    writeStoredState({
      viewMode,
      date: currentDate.format('YYYY-MM-DD'),
      showOnlyOpen,
      showMyAppointmentsOnly,
    });
  }, [viewMode, currentDate, showOnlyOpen, showMyAppointmentsOnly]);

  const tabs: { id: ViewMode; label: string }[] = [
    { id: 'month', label: 'Month' },
    { id: 'week', label: 'Week' },
    { id: 'day', label: 'Day' },
  ];

  const weekStart = currentDate.startOf('isoWeek');
  const weekEnd = weekStart.add(6, 'day');
  const dateLabel =
    viewMode === 'month'
      ? currentDate.format('MMMM YYYY')
      : viewMode === 'week'
        ? `${weekStart.format('D')}–${weekEnd.format('D MMM YYYY')}`
        : currentDate.format('ddd, D MMM YYYY');

  const setCurrentDateTracked = useCallback((d: Dayjs | ((prev: Dayjs) => Dayjs)) => {
    setCurrentDate((prev) => (typeof d === 'function' ? d(prev) : d));
  }, []);

  const handlePrevious = () =>
    setCurrentDate((d) =>
      viewMode === 'month' ? d.subtract(1, 'month') : viewMode === 'week' ? d.subtract(1, 'week') : d.subtract(1, 'day'),
    );
  const handleNext = () =>
    setCurrentDate((d) =>
      viewMode === 'month' ? d.add(1, 'month') : viewMode === 'week' ? d.add(1, 'week') : d.add(1, 'day'),
    );
  const handleToday = () => setCurrentDate(dayjs().startOf('day'));

  const sharedViewProps = {
    calendarMode: 'appointments' as const,
    currentDate,
    setCurrentDate: setCurrentDateTracked,
    showOnlyOpen,
    showMyAppointmentsOnly,
    embeddedInCalendar: true,
  };

  return (
    <div className={cn('flex flex-col h-full', colors.background)}>
      <div
        className={cn(
          'shrink-0 border-b flex flex-nowrap items-center gap-4 px-4 py-3 overflow-x-auto',
          colors.border,
          colors.surfaceHover,
        )}
      >
        <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', colors.info)}>
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <div className="flex rounded-lg border overflow-hidden shrink-0" role="tablist" aria-label="Calendar view">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={viewMode === tab.id}
              onClick={() => setViewMode(tab.id)}
              className={cn(
                'px-4 py-2 text-sm font-medium transition-colors',
                viewMode === tab.id ? cn(colors.primary, colors.primaryText) : cn(colors.text, colors.interactiveHover),
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          <button
            onClick={handlePrevious}
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors',
              colors.border,
              colors.interactiveHover,
            )}
            aria-label={viewMode === 'month' ? 'Previous month' : viewMode === 'week' ? 'Previous week' : 'Previous day'}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className={cn('min-w-[120px] sm:min-w-[160px] rounded-lg border px-3 py-2 text-center shrink-0', colors.border, colors.surface)}>
            <span className={cn('text-sm font-semibold whitespace-nowrap', colors.text)}>{dateLabel}</span>
          </div>
          <button
            onClick={handleNext}
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors',
              colors.border,
              colors.interactiveHover,
            )}
            aria-label={viewMode === 'month' ? 'Next month' : viewMode === 'week' ? 'Next week' : 'Next day'}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={handleToday}
            className={cn(
              'shrink-0 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
              colors.info,
              colors.interactiveHover,
            )}
          >
            Today
          </button>
        </div>
        <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none ml-4">
          <Switch
            checked={showOnlyOpen}
            onChange={(e) => setShowOnlyOpen(e.target.checked)}
            size="sm"
            aria-label="Show only open appointments"
          />
          <span className={cn('text-sm font-medium whitespace-nowrap', colors.text)}>Open only</span>
        </label>
        <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none ml-2">
          <Switch
            checked={showMyAppointmentsOnly}
            onChange={(e) => setShowMyAppointmentsOnly(e.target.checked)}
            size="sm"
            aria-label="My appointments only"
          />
          <span className={cn('text-sm font-medium whitespace-nowrap', colors.text)}>My appointments only</span>
        </label>
        <div className="flex items-center gap-2 ml-auto shrink-0">
          <button
            onClick={() => navigate('/appointments/new')}
            className={cn(
              'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-all hover:opacity-90',
              colors.primary,
              colors.primaryText,
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400/50 dark:focus:ring-gray-500/50 dark:focus:ring-offset-gray-900',
            )}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Appointment
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {viewMode === 'month' && <TodosMonthlyView {...sharedViewProps} />}
        {viewMode === 'week' && <TodosWeeklyView {...sharedViewProps} />}
        {viewMode === 'day' && <TodosDailyView {...sharedViewProps} />}
      </div>
    </div>
  );
};

export default CalendarView;
